import React, { useCallback, useMemo, useState } from "react";
import { TutorialContext } from "@/contexts/TutorialContext";
import { STEPS } from "@/components/tutorial/tutorialSteps";
const MAX_STEP = STEPS.length - 1;
const HINT_SEEN_KEY = "tutorial_reopen_hint_seen";
const TUTORIAL_SHOWN_KEY = "tutorial_auto_shown";

function clampStep(index: number): number {
    return Math.max(0, Math.min(MAX_STEP, index));
}

function hasSeenHint(): boolean {
    return localStorage.getItem(HINT_SEEN_KEY) === "true";
}

function markHintAsSeen(): void {
    localStorage.setItem(HINT_SEEN_KEY, "true");
}

function hasTutorialBeenShown(): boolean {
    return localStorage.getItem(TUTORIAL_SHOWN_KEY) === "true";
}

function markTutorialAsShown(): void {
    localStorage.setItem(TUTORIAL_SHOWN_KEY, "true");
}

export function TutorialProvider({ children }: { children: React.ReactNode }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isCardDismissed, setIsCardDismissed] = useState(true);
    const [showReopenHint, setShowReopenHint] = useState(false);

    const clearReopenHint = useCallback(() => {
        setShowReopenHint(false);
    }, []);

    const open = useCallback(() => {
        setCurrentStep(0);
        setIsCardDismissed(false);
        clearReopenHint();
    }, [clearReopenHint]);

    const openFirstTime = useCallback(() => {
        if (!hasTutorialBeenShown()) {
            markTutorialAsShown();
            setCurrentStep(0);
            setIsCardDismissed(false);
            clearReopenHint();
        }
    }, [clearReopenHint]);

    const goTo = useCallback((index: number) => {
        setCurrentStep(clampStep(index));
    }, []);

    const dismissCard = useCallback(() => {
        setIsCardDismissed(true);
        if (!hasSeenHint()) {
            setShowReopenHint(true);
            markHintAsSeen();
        }
    }, []);

    const value = useMemo(
        () => ({
            currentStep,
            isCardDismissed,
            showReopenHint,
            open,
            openFirstTime,
            goTo,
            dismissCard,
            clearReopenHint
        }),
        [
            currentStep,
            isCardDismissed,
            showReopenHint,
            open,
            openFirstTime,
            goTo,
            dismissCard,
            clearReopenHint
        ]
    );

    return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
}
